import './builder.css';
import { byNumericId, idSetSorted } from '../shared/id-sort.ts';
import { escapeHtml } from '../shared/escape-html.ts';
import { parseConfigFromText, applyParsedConfig } from '../shared/parse-config.ts';
import { generateConfigJs, generateCmsSnippet } from '../shared/generate-config.ts';
import { validateConfig } from '../shared/validate-config.ts';
import { parseLoketTicketApiResponse } from '../shared/parse-loket-ticket-api.ts';
import { mergeLoketImport } from '../shared/merge-loket-import.ts';
import type { BuilderState } from '../shared/schema.ts';
import type { LoketTicketApiImport } from '../shared/parse-loket-ticket-api.ts';
import type { MergeSummary } from '../shared/merge-loket-import.ts';

const STORAGE_KEY = 'addonFilterBuilderDraft';
const STEP_COUNT = 5;

const DEFAULT_STATE: BuilderState = {
  activeTicketGroupId: '',
  engineUrl: 'https://src.rizarma.com/addon-filter-engine.js',
  cssUrl: 'https://src.rizarma.com/filter-addons-by-ticket-date.css',
  categories: [],
  defaultOpenCategories: [],
  activeTicketAddonMap: {},
  addonCategoryById: {},
  selectedTicketId: null,
  selectedAddonIds: new Set(),
  selectedAddonTableIds: new Set(),
  ticketNamesById: {},
  addonNamesById: {},
};

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function loadState(): Partial<BuilderState> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      activeTicketGroupId: String(parsed.activeTicketGroupId || ''),
      engineUrl: String(parsed.engineUrl || DEFAULT_STATE.engineUrl),
      cssUrl: String(parsed.cssUrl || DEFAULT_STATE.cssUrl),
      categories: Array.isArray(parsed.categories) ? parsed.categories : [],
      defaultOpenCategories: Array.isArray(parsed.defaultOpenCategories) ? parsed.defaultOpenCategories : [],
      activeTicketAddonMap: isPlainObject(parsed.activeTicketAddonMap) ? parsed.activeTicketAddonMap : {},
      addonCategoryById: isPlainObject(parsed.addonCategoryById) ? parsed.addonCategoryById : {},
      ticketNamesById: isPlainObject(parsed.ticketNamesById) ? parsed.ticketNamesById : {},
      addonNamesById: isPlainObject(parsed.addonNamesById) ? parsed.addonNamesById : {},
    };
  } catch {
    return null;
  }
}

function normalizeEngineUrl(url: string): string {
  if (url.includes('addon-filter-engine-v')) return DEFAULT_STATE.engineUrl;
  return url;
}
function normalizeCssUrl(url: string): string {
  if (url.includes('filter-addons-by-ticket-date.css?v=')) return DEFAULT_STATE.cssUrl;
  return url;
}

function createState(): BuilderState {
  const saved = loadState();
  return {
    ...DEFAULT_STATE,
    ...saved,
    engineUrl: normalizeEngineUrl(saved?.engineUrl || DEFAULT_STATE.engineUrl),
    cssUrl: normalizeCssUrl(saved?.cssUrl || DEFAULT_STATE.cssUrl),
    selectedTicketId: null,
    selectedAddonIds: new Set(),
    selectedAddonTableIds: new Set(),
  };
}

let state = createState();
let currentStep = 1;
let activeAddonCategoryKey: string | null = null;

function getAllAddonIds(): string[] {
  const ids = new Set<string>();
  for (const ticketId of Object.keys(state.activeTicketAddonMap)) {
    for (const addonId of state.activeTicketAddonMap[ticketId] || []) {
      ids.add(String(addonId));
    }
  }
  return idSetSorted([...ids]);
}

function getAddonIdsForTicket(ticketId: string): Set<string> {
  return new Set((state.activeTicketAddonMap[ticketId] || []).map(String));
}

function getCategoryKeys(): string[] {
  return state.categories.map(c => c.key).filter(Boolean);
}

function getCategoryLabel(key: string): string {
  const cat = state.categories.find(c => c.key === key);
  return cat ? cat.label : '';
}
function getTicketDisplayName(ticketId: string): string {
  return state.ticketNamesById[ticketId] || '';
}
function getAddonDisplayName(addonId: string): string {
  return state.addonNamesById[addonId] || '';
}
function getAddonMappingStatusFilter(): 'all' | 'mapped' | 'unmapped' {
  const el = document.getElementById('addonMappingStatusFilter') as HTMLSelectElement | null;
  const value = el ? el.value : 'all';
  if (value === 'mapped' || value === 'unmapped') return value;
  return 'all';
}
function getFilteredAddonIds(): string[] {
  const addonSearchEl = document.getElementById('addonSearch') as HTMLInputElement | null;
  const search = addonSearchEl ? addonSearchEl.value.trim().toLowerCase() : '';
  const statusFilter = getAddonMappingStatusFilter();
  const allAddons = idSetSorted([...getAllAddonIds(), ...Object.keys(state.addonCategoryById)]);
  const selectedAddons = state.selectedTicketId ? getAddonIdsForTicket(state.selectedTicketId) : new Set<string>();
  if (!search && statusFilter === 'all') return allAddons;
  return allAddons.filter(addonId => {
    const displayName = getAddonDisplayName(addonId);
    const matchesSearch = !search || addonId.includes(search) || displayName.toLowerCase().includes(search);
    if (!matchesSearch) return false;
    if (statusFilter === 'all') return true;
    const isMapped = selectedAddons.has(addonId);
    return statusFilter === 'mapped' ? isMapped : !isMapped;
  });
}
function isDefaultOpen(key: string): boolean {
  return state.defaultOpenCategories.includes(key);
}

function addTicket(ticketId: string | number) {
  const id = String(ticketId).trim();
  if (!id || state.activeTicketAddonMap[id] !== undefined) return;
  state.activeTicketAddonMap[id] = [];
  state.selectedTicketId = id;
}

function removeTicket(ticketId: string) {
  delete state.activeTicketAddonMap[ticketId];
  if (state.selectedTicketId === ticketId) {
    const remaining = Object.keys(state.activeTicketAddonMap).sort(byNumericId);
    state.selectedTicketId = remaining[0] || null;
  }
}
function applyLoketImport(parsed: LoketTicketApiImport): MergeSummary {
  return mergeLoketImport(state, parsed);
}

function toggleAddonForTicket(ticketId: string, addonId: string, checked: boolean) {
  const list = state.activeTicketAddonMap[ticketId] || [];
  const set = new Set(list.map(String));
  if (checked) set.add(String(addonId));
  else set.delete(String(addonId));
  state.activeTicketAddonMap[ticketId] = idSetSorted([...set]);
}

function addAddon(addonId: string | number, categoryKey?: string) {
  const id = String(addonId).trim();
  if (!id) return;
  if (!state.addonCategoryById[id]) {
    state.addonCategoryById[id] = categoryKey || getCategoryKeys()[0] || '';
  }
}

function removeAddon(addonId: string) {
  const id = String(addonId);
  delete state.addonCategoryById[id];
  for (const ticketId of Object.keys(state.activeTicketAddonMap)) {
    const list = state.activeTicketAddonMap[ticketId] || [];
    state.activeTicketAddonMap[ticketId] = list.filter(a => String(a) !== id);
  }
}

function setAddonCategory(addonId: string, categoryKey: string) {
  state.addonCategoryById[String(addonId)] = categoryKey;
}

function addCategory(key: string, label?: string, openDefault?: boolean) {
  const k = (key || '').trim();
  if (!k || state.categories.some(c => c.key === k)) return;
  state.categories.push({ key: k, label: (label || '').trim() || k, openDefault: !!openDefault });
}

function removeCategory(key: string) {
  state.categories = state.categories.filter(c => c.key !== key);
  state.defaultOpenCategories = state.defaultOpenCategories.filter(k => k !== key);
  for (const addonId of Object.keys(state.addonCategoryById)) {
    if (state.addonCategoryById[addonId] === key) {
      delete state.addonCategoryById[addonId];
    }
  }
}

function saveState() {
  try {
    const payload = JSON.stringify({
      activeTicketGroupId: state.activeTicketGroupId,
      engineUrl: state.engineUrl,
      cssUrl: state.cssUrl,
      categories: state.categories,
      defaultOpenCategories: state.defaultOpenCategories,
      activeTicketAddonMap: state.activeTicketAddonMap,
      addonCategoryById: state.addonCategoryById,
      ticketNamesById: state.ticketNamesById,
      addonNamesById: state.addonNamesById,
    });
    localStorage.setItem(STORAGE_KEY, payload);
  } catch {
    /* ignore */
  }
}

function generateConfigObject() {
  const sortedMap: Record<string, string[]> = {};
  for (const ticketId of Object.keys(state.activeTicketAddonMap).sort(byNumericId)) {
    sortedMap[ticketId] = idSetSorted(state.activeTicketAddonMap[ticketId]);
  }
  const sortedCat: Record<string, string> = {};
  for (const addonId of Object.keys(state.addonCategoryById).sort(byNumericId)) {
    sortedCat[addonId] = state.addonCategoryById[addonId];
  }
  const labels: Record<string, string> = {};
  for (const c of state.categories) {
    if (c.key) labels[c.key] = c.label;
  }
  return {
    activeTicketGroupId: state.activeTicketGroupId || '',
    activeTicketAddonMap: sortedMap,
    defaultOpenCategories: state.defaultOpenCategories,
    addonCategoryLabels: labels,
    addonCategoryById: sortedCat,
  };
}

function generateConfigJsLocal(): string {
  return generateConfigJs(generateConfigObject());
}

function generateCmsSnippetLocal(): string {
  const config = generateConfigObject();
  return generateCmsSnippet(config, state.engineUrl, state.cssUrl);
}

function validate(): { errors: string[]; warnings: string[] } {
  const config = generateConfigObject();
  return validateConfig(config);
}

function renderWizardNav() {
  const nav = document.getElementById('wizardNav');
  if (!nav) return;
  nav.innerHTML = '';
  const labels = ['Import', 'Groups', 'Mapping', 'Review', 'Output'];
  for (let i = 1; i <= STEP_COUNT; i++) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = `${i}. ${labels[i - 1]}`;
    btn.setAttribute('aria-label', `Step ${i}: ${labels[i - 1]}`);
    btn.setAttribute('aria-controls', `step${i}`);
    if (i === currentStep) {
      btn.classList.add('active');
      btn.setAttribute('aria-current', 'step');
    } else if (i < currentStep) {
      btn.classList.add('done');
    }
    btn.addEventListener('click', () => goToStep(i));
    nav.appendChild(btn);
  }
}

function goToStep(n: number, skipSave?: boolean) {
  if (n < 1 || n > STEP_COUNT) return;
  for (let i = 1; i <= STEP_COUNT; i++) {
    const el = document.getElementById(`step${i}`);
    if (!el) continue;
    const isActive = i === n;
    el.classList.toggle('active', isActive);
    if (isActive) {
      el.removeAttribute('hidden');
      el.removeAttribute('aria-hidden');
    } else {
      el.setAttribute('hidden', '');
      el.setAttribute('aria-hidden', 'true');
    }
  }
  currentStep = n;
  renderWizardNav();
  renderCurrentStep();
  if (!skipSave) saveState();
}

function renderMappingStep() {
  const ticketGroupId = document.getElementById('ticketGroupId') as HTMLInputElement | null;
  if (ticketGroupId) ticketGroupId.value = state.activeTicketGroupId;
  const ticketList = document.getElementById('ticketList');
  const ticketIds = Object.keys(state.activeTicketAddonMap).sort(byNumericId);
  const ticketSearch = document.getElementById('ticketSearch') as HTMLInputElement | null;
  const search = ticketSearch ? ticketSearch.value.trim().toLowerCase() : '';
  if (ticketList) {
    ticketList.innerHTML = '';
    for (const ticketId of ticketIds) {
      const displayName = getTicketDisplayName(ticketId);
      if (search && !ticketId.includes(search) && !displayName.toLowerCase().includes(search)) continue;
      const row = document.createElement('div');
      const isSelected = state.selectedTicketId === ticketId;
      row.className = 'ticket-row' + (isSelected ? ' selected' : '');
      row.setAttribute('role', 'button');
      row.setAttribute('tabIndex', '0');
      row.setAttribute('aria-pressed', String(isSelected));
      const count = (state.activeTicketAddonMap[ticketId] || []).length;
      const nameHtml = displayName ? `<span class="ticket-name">${escapeHtml(displayName)}</span>` : '';
      const activePill = isSelected ? `<span class="active-pill">Active</span>` : '';
      row.innerHTML = `
        <input type="checkbox" class="ticket-select" data-id="${escapeHtml(ticketId)}" aria-label="Mark ticket ${escapeHtml(ticketId)} for removal">
        <div class="ticket-info">
          <span class="ticket-id">${escapeHtml(ticketId)}</span>
          ${nameHtml}
        </div>
        ${activePill}
        <span class="addon-count">${count} addon${count !== 1 ? 's' : ''}</span>
      `;
      const handleActivate = () => {
        state.selectedTicketId = ticketId;
        renderMappingStep();
      };
      row.addEventListener('click', e => {
        if ((e.target as HTMLElement).closest('input, select, button')) return;
        handleActivate();
      });
      row.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleActivate();
        }
      });
      ticketList.appendChild(row);
    }
    if (ticketIds.length === 0) {
      ticketList.innerHTML = '<div class="empty-state">No tickets. Add one or import data.</div>';
    }
  }
  const addonContainer = document.getElementById('addonCategories');
  if (!addonContainer) return;
  addonContainer.innerHTML = '';
  const addonSearchEl = document.getElementById('addonSearch') as HTMLInputElement | null;
  const addonSearch = addonSearchEl ? addonSearchEl.value.trim().toLowerCase() : '';
  const statusFilter = getAddonMappingStatusFilter();
  const allAddons = idSetSorted([...getAllAddonIds(), ...Object.keys(state.addonCategoryById)]);
  const selectedAddons = state.selectedTicketId ? getAddonIdsForTicket(state.selectedTicketId) : new Set();
  const mappingSummary = document.getElementById('mappingSummary');
  if (mappingSummary) {
    if (!state.selectedTicketId) {
      mappingSummary.innerHTML = '';
    } else {
      const mappedCount = selectedAddons.size;
      const totalAddons = allAddons.length;
      const ticketName = getTicketDisplayName(state.selectedTicketId);
      const ticketLabel = ticketName ? `${escapeHtml(state.selectedTicketId)} — ${escapeHtml(ticketName)}` : escapeHtml(state.selectedTicketId);
      const filterParts: string[] = [];
      if (addonSearch) filterParts.push('search');
      if (statusFilter !== 'all') filterParts.push(statusFilter);
      const filterHint = filterParts.length ? `<span class="mapping-scope-hint">Showing ${filterParts.join(' + ')} results only</span>` : '';
      mappingSummary.innerHTML = `<div class="mapping-summary-inner">${ticketLabel}: <strong>${mappedCount}</strong> of ${totalAddons} addons mapped</div>${filterHint}`;
    }
  }
  if (!state.selectedTicketId) {
    addonContainer.innerHTML = `
      <div class="empty-state actionable">
        <p>Select a ticket from the left panel to see and map its addons.</p>
      </div>
    `;
    return;
  }
  if (allAddons.length === 0) {
    addonContainer.innerHTML = `
      <div class="empty-state actionable">
        <p>No addons yet. Add addons and groups in the Groups step, then return here to assign them to tickets.</p>
        <button type="button" class="btn-secondary" id="jumpToStep4Btn">Go to Groups — Add Addons &amp; Groups</button>
      </div>
    `;
    const jumpBtn = document.getElementById('jumpToStep4Btn');
    if (jumpBtn) {
      jumpBtn.addEventListener('click', () => goToStep(2));
    }
    return;
  }
  const byCategory: Record<string, string[]> = {};
  const filteredByCategory: Record<string, string[]> = {};
  for (const addonId of allAddons) {
    const cat = state.addonCategoryById[addonId] || 'Uncategorized';
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push(addonId);
  }
  const categoryKeys = Object.keys(byCategory).sort();
  for (const catKey of categoryKeys) {
    filteredByCategory[catKey] = byCategory[catKey].filter(addonId => {
      const displayName = getAddonDisplayName(addonId);
      const matchesSearch = !addonSearch || addonId.includes(addonSearch) || displayName.toLowerCase().includes(addonSearch);
      if (!matchesSearch) return false;
      if (statusFilter === 'all') return true;
      const isMapped = selectedAddons.has(addonId);
      return statusFilter === 'mapped' ? isMapped : !isMapped;
    });
  }
  if (!activeAddonCategoryKey || !byCategory[activeAddonCategoryKey]) {
    activeAddonCategoryKey = categoryKeys[0] || null;
  }
  const tabsWrap = document.createElement('div');
  tabsWrap.className = 'addon-category-tabs';
  tabsWrap.setAttribute('role', 'tablist');
  tabsWrap.setAttribute('aria-label', 'Addon groups');
  for (const catKey of categoryKeys) {
    const label = getCategoryLabel(catKey) || catKey;
    const count = filteredByCategory[catKey].length;
    const total = byCategory[catKey].length;
    const btn = document.createElement('button');
    const isActive = activeAddonCategoryKey === catKey;
    btn.className = 'addon-category-tab' + (isActive ? ' active' : '');
    btn.type = 'button';
    btn.setAttribute('role', 'tab');
    btn.setAttribute('aria-selected', String(isActive));
    const tabId = `addon-tab-${catKey}`;
    const panelId = `addon-panel-${catKey}`;
    btn.id = tabId;
    btn.setAttribute('aria-controls', panelId);
    btn.innerHTML = `<span class="tab-label">${escapeHtml(label)}</span> <span class="tab-count">${count}/${total}</span>`;
    btn.addEventListener('click', () => {
      activeAddonCategoryKey = catKey;
      renderMappingStep();
    });
    tabsWrap.appendChild(btn);
  }
  addonContainer.appendChild(tabsWrap);
  const actionsWrap = document.createElement('div');
  actionsWrap.className = 'addon-category-panel-actions';
  actionsWrap.innerHTML = `
    <button type="button" class="btn-secondary" id="selectGroupBtn">Map this group</button>
    <button type="button" class="btn-secondary" id="deselectGroupBtn">Unmap this group</button>
  `;
  addonContainer.appendChild(actionsWrap);
  const selectGroupBtn = actionsWrap.querySelector('#selectGroupBtn') as HTMLButtonElement | null;
  const deselectGroupBtn = actionsWrap.querySelector('#deselectGroupBtn') as HTMLButtonElement | null;
  if (selectGroupBtn) {
    selectGroupBtn.addEventListener('click', () => {
      if (!state.selectedTicketId || !activeAddonCategoryKey) return;
      const ids = filteredByCategory[activeAddonCategoryKey] || [];
      for (const addonId of ids) {
        if (!selectedAddons.has(addonId)) {
          toggleAddonForTicket(state.selectedTicketId, addonId, true);
        }
      }
      commitState({ render: 'all' });
    });
  }
  if (deselectGroupBtn) {
    deselectGroupBtn.addEventListener('click', () => {
      if (!state.selectedTicketId || !activeAddonCategoryKey) return;
      const ids = filteredByCategory[activeAddonCategoryKey] || [];
      for (const addonId of ids) {
        if (selectedAddons.has(addonId)) {
          toggleAddonForTicket(state.selectedTicketId, addonId, false);
        }
      }
      commitState({ render: 'all' });
    });
  }
  const panel = document.createElement('div');
  panel.className = 'addon-category-panel';
  const activeTabId = activeAddonCategoryKey ? `addon-tab-${activeAddonCategoryKey}` : '';
  const activePanelId = activeAddonCategoryKey ? `addon-panel-${activeAddonCategoryKey}` : '';
  panel.setAttribute('role', 'tabpanel');
  panel.id = activePanelId;
  panel.setAttribute('aria-labelledby', activeTabId);
  const activeIds = filteredByCategory[activeAddonCategoryKey || ''] || [];
  if (activeIds.length === 0) {
    panel.innerHTML = `<div class="empty-state">No addons match the current filters in this group.</div>`;
  } else {
    if (addonSearch || statusFilter !== 'all') {
      const scopeHint = document.createElement('div');
      scopeHint.className = 'mapping-scope-hint';
      const parts: string[] = [];
      if (addonSearch) parts.push('search');
      if (statusFilter !== 'all') parts.push(statusFilter);
      scopeHint.textContent = `Actions below apply only to currently visible ${parts.join(' + ')} results.`;
      panel.appendChild(scopeHint);
    }
    for (const addonId of activeIds.sort(byNumericId)) {
      const displayName = getAddonDisplayName(addonId);
      const row = document.createElement('div');
      const isMapped = selectedAddons.has(addonId);
      row.className = 'addon-checkbox-row' + (isMapped ? ' mapped' : ' unmapped');
      row.setAttribute('role', 'checkbox');
      row.setAttribute('tabIndex', '0');
      row.setAttribute('aria-checked', String(isMapped));
      const hasCategory = !!state.addonCategoryById[addonId];
      const categoryOptions = state.categories.map(c => `<option value="${escapeHtml(c.key)}">${escapeHtml(c.label || c.key)}</option>`).join('');
      const nameHtml = displayName ? `<span class="addon-name">${escapeHtml(displayName)}</span>` : '';
      row.innerHTML = `
        <input type="checkbox" data-addon="${escapeHtml(addonId)}" ${isMapped ? 'checked' : ''} aria-label="${isMapped ? 'Unmap' : 'Map'} addon ${escapeHtml(addonId)}">
        <div class="addon-info">
          <span class="addon-id">${escapeHtml(addonId)}</span>
          ${nameHtml}
        </div>
        ${!hasCategory ? (state.categories.length === 0
          ? `<span class="no-groups-hint">No groups yet. <button type="button" class="btn-text goto-step4">Create in Groups</button></span>`
          : `<select class="inline-category-select" data-addon="${escapeHtml(addonId)}" aria-label="Group for addon ${escapeHtml(addonId)}"><option value="">Choose group</option>${categoryOptions}</select>`) : ''}
        <span class="addon-status ${isMapped ? 'mapped' : 'unmapped'}">${isMapped ? 'mapped' : 'unmapped'}</span>
      `;
      const toggleMapping = () => {
        if (!state.selectedTicketId) return;
        const newValue = !selectedAddons.has(addonId);
        toggleAddonForTicket(state.selectedTicketId, addonId, newValue);
        commitState({ render: 'all' });
      };
      row.addEventListener('click', e => {
        const target = e.target as HTMLElement;
        if (target.closest('input, select, button')) return;
        toggleMapping();
      });
      row.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleMapping();
        }
      });
      const checkbox = row.querySelector('input[type="checkbox"]');
      if (checkbox) {
        checkbox.addEventListener('change', e => {
          if (!state.selectedTicketId) return;
          toggleAddonForTicket(state.selectedTicketId, addonId, (e.target as HTMLInputElement).checked);
          commitState({ render: 'all' });
        });
      }
      const catSelect = row.querySelector('.inline-category-select') as HTMLSelectElement | null;
      if (catSelect) {
        catSelect.addEventListener('change', e => {
          const value = (e.target as HTMLSelectElement).value;
          if (!value) return;
          setAddonCategory(addonId, value);
          if (state.selectedTicketId && !selectedAddons.has(addonId)) {
            toggleAddonForTicket(state.selectedTicketId, addonId, true);
          }
          commitState({ render: 'all' });
        });
      }
      const gotoBtns = row.querySelectorAll('.goto-step4');
      gotoBtns.forEach(btn => btn.addEventListener('click', () => goToStep(2)));
      panel.appendChild(row);
    }
  }
  addonContainer.appendChild(panel);
}

function renderGroupsStep() {
  const defaultOpenEl = document.getElementById('defaultOpenCategories') as HTMLInputElement | null;
  if (defaultOpenEl) defaultOpenEl.value = state.defaultOpenCategories.join(', ');

  const list = document.getElementById('categoryList');
  if (list) {
    list.innerHTML = '';
    if (state.categories.length === 0) {
      list.innerHTML = '<div class="empty-state">No groups yet. <strong>Groups are required</strong> before you can categorize addons. Add your first group below.</div>';
    } else {
      for (let i = 0; i < state.categories.length; i++) {
        const c = state.categories[i];
        const row = document.createElement('div');
        row.className = 'category-row';
        const keyId = `cat-key-${i}`;
        const labelId = `cat-label-${i}`;
        const openId = `cat-open-${i}`;
        row.innerHTML = `
          <div class="group-row-top">
            <span class="group-row-badge" aria-hidden="true">${i + 1}</span>
            <button type="button" class="btn-danger group-remove-btn" data-action="remove" aria-label="Remove group ${escapeHtml(c.label || c.key)}">\u2715</button>
          </div>
          <div class="group-fields-grid">
            <div class="group-field">
              <label for="${keyId}">Key</label>
              <input type="text" id="${keyId}" placeholder="e.g. food" value="${escapeHtml(c.key)}" data-field="key">
            </div>
            <div class="group-field">
              <label for="${labelId}">Display name</label>
              <input type="text" id="${labelId}" placeholder="e.g. Food & Drinks" value="${escapeHtml(c.label)}" data-field="label">
            </div>
          </div>
          <div class="group-default-toggle">
            <label class="toggle-label" for="${openId}">Open by default</label>
            <label class="toggle-switch" aria-hidden="true">
              <input type="checkbox" id="${openId}" ${c.openDefault ? 'checked' : ''} data-field="openDefault">
              <span class="toggle-track"><span class="toggle-thumb"></span></span>
            </label>
          </div>
        `;
        const keyInput = row.querySelector('[data-field="key"]') as HTMLInputElement | null;
        if (keyInput) {
          keyInput.addEventListener('change', e => {
            const oldKey = c.key;
            c.key = (e.target as HTMLInputElement).value.trim();
            for (const addonId of Object.keys(state.addonCategoryById)) {
              if (state.addonCategoryById[addonId] === oldKey) {
                state.addonCategoryById[addonId] = c.key;
              }
            }
            state.defaultOpenCategories = state.defaultOpenCategories.map(k => k === oldKey ? c.key : k);
            commitState({ render: 'all' });
          });
        }
        const labelInput = row.querySelector('[data-field="label"]') as HTMLInputElement | null;
        if (labelInput) {
          labelInput.addEventListener('change', e => {
            c.label = (e.target as HTMLInputElement).value.trim();
            commitState({ render: 'all' });
          });
        }
        const openDefaultInput = row.querySelector('[data-field="openDefault"]') as HTMLInputElement | null;
        if (openDefaultInput) {
          openDefaultInput.addEventListener('change', e => {
            c.openDefault = (e.target as HTMLInputElement).checked;
            if (c.openDefault && !state.defaultOpenCategories.includes(c.key)) {
              state.defaultOpenCategories.push(c.key);
            } else if (!c.openDefault) {
              state.defaultOpenCategories = state.defaultOpenCategories.filter(k => k !== c.key);
            }
            const defaultOpenInput = document.getElementById('defaultOpenCategories') as HTMLInputElement | null;
            if (defaultOpenInput) defaultOpenInput.value = state.defaultOpenCategories.join(', ');
            commitState({ render: 'all' });
          });
        }
        const removeBtn = row.querySelector('[data-action="remove"]');
        if (removeBtn) {
          removeBtn.addEventListener('click', () => {
            removeCategory(c.key);
            commitState({ render: 'all' });
          });
        }
        list.appendChild(row);
      }
    }
  }

  const bulk = document.getElementById('bulkCategory') as HTMLSelectElement | null;
  if (bulk) {
    bulk.innerHTML = '<option value="">-- bulk assign --</option>';
    for (const c of state.categories) {
      const opt = document.createElement('option');
      opt.value = c.key;
      opt.textContent = c.label || c.key;
      bulk.appendChild(opt);
    }
  }

  renderAddonTable();
}

function renderAddonTable() {
  const table = document.getElementById('addonTable');
  if (!table) return;

  const checkedIds = new Set(
    Array.from(table.querySelectorAll('.addon-table-select:checked')).map(ch => (ch as HTMLElement).dataset.addon || '')
  );

  table.innerHTML = '';
  const allAddonIds = [...Object.keys(state.addonCategoryById).sort(byNumericId), ...getAllAddonIds().filter(id => !state.addonCategoryById[id])];
  const uniqueIds = idSetSorted(allAddonIds);

  const searchEl = document.getElementById('addonGroupSearch') as HTMLInputElement | null;
  const filter = searchEl ? searchEl.value.trim().toLowerCase() : '';
  const statusFilterEl = document.getElementById('addonCategoryStatusFilter') as HTMLSelectElement | null;
  const statusFilter = statusFilterEl ? statusFilterEl.value : 'all';
  const filteredIds = uniqueIds.filter(id => {
    if (filter) {
      const displayName = getAddonDisplayName(id);
      if (!id.toLowerCase().includes(filter) && !displayName.toLowerCase().includes(filter)) return false;
    }
    if (statusFilter === 'categorized') return !!state.addonCategoryById[id];
    if (statusFilter === 'uncategorized') return !state.addonCategoryById[id];
    return true;
  });

  for (const addonId of filteredIds) {
    const row = document.createElement('div');
    row.className = 'addon-table-row';
    const cat = state.addonCategoryById[addonId] || '';
    let usedCount = 0;
    for (const ticketId of Object.keys(state.activeTicketAddonMap)) {
      if ((state.activeTicketAddonMap[ticketId] || []).includes(addonId)) usedCount++;
    }
    const unmapped = !cat;
    const displayName = getAddonDisplayName(addonId);
    const nameHtml = displayName ? `<span class="addon-name">${escapeHtml(displayName)}</span>` : '';
    const isChecked = checkedIds.has(addonId);

    row.innerHTML = `
      <label class="addon-table-select-label">
        <input type="checkbox" data-addon="${escapeHtml(addonId)}" class="addon-table-select" aria-label="Select addon ${escapeHtml(addonId)}" ${isChecked ? 'checked' : ''}>
      </label>
      <div class="addon-table-info">
        <span class="addon-id">${escapeHtml(addonId)}${unmapped ? '<span class="unmapped-badge">uncategorized</span>' : ''}${nameHtml}</span>
      </div>
      <div class="addon-table-group">
        <select data-addon="${escapeHtml(addonId)}" aria-label="Group for addon ${escapeHtml(addonId)}">
          ${state.categories.map(c => `<option value="${escapeHtml(c.key)}" ${cat === c.key ? 'selected' : ''}>${escapeHtml(c.label || c.key)}</option>`).join('')}
          <option value="" ${!cat ? 'selected' : ''}>-- none --</option>
        </select>
      </div>
      <span class="used-count">${usedCount} ticket${usedCount !== 1 ? 's' : ''}</span>
      <button type="button" class="btn-danger" data-action="remove" data-addon="${escapeHtml(addonId)}" aria-label="Remove addon ${escapeHtml(addonId)}">&times;</button>
    `;
    if (isChecked) row.classList.add('selected');

    const select = row.querySelector('select') as HTMLSelectElement | null;
    if (select) {
      select.addEventListener('change', e => {
        setAddonCategory(addonId, (e.target as HTMLSelectElement).value);
        commitState({ render: 'all' });
      });
    }
    const removeBtn = row.querySelector('[data-action="remove"]');
    if (removeBtn) {
      removeBtn.addEventListener('click', () => {
        removeAddon(addonId);
        commitState({ render: 'all' });
      });
    }
    const checkbox = row.querySelector('.addon-table-select') as HTMLInputElement | null;
    if (checkbox) {
      checkbox.addEventListener('change', () => {
        row.classList.toggle('selected', checkbox.checked);
      });
    }
    row.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('.addon-table-group') || target.closest('button') || target.closest('.addon-table-select-label')) return;
      if (checkbox) {
        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    table.appendChild(row);
  }
  if (filteredIds.length === 0) {
    const emptyMessage = uniqueIds.length === 0
      ? 'No addons. Add addons or import data.'
      : 'No addons match the current filters.';
    table.innerHTML = `<div class="empty-state">${emptyMessage}</div>`;
  }
}

function renderReview() {
  const ticketCount = Object.keys(state.activeTicketAddonMap).length;
  const addonCount = getAllAddonIds().length;
  const catCount = state.categories.length;
  const openCount = state.defaultOpenCategories.length;

  const grid = document.getElementById('summaryGrid');
  if (grid) {
    grid.innerHTML = `
      <div class="summary-card"><div class="value">${ticketCount}</div><div class="label">Tickets</div></div>
      <div class="summary-card"><div class="value">${addonCount}</div><div class="label">Addons</div></div>
      <div class="summary-card"><div class="value">${catCount}</div><div class="label">Groups</div></div>
      <div class="summary-card"><div class="value">${openCount}</div><div class="label">Default Open</div></div>
    `;
  }

  const { errors, warnings } = validate();
  const errorsList = document.getElementById('errorsList');
  const warningsList = document.getElementById('warningsList');
  if (errorsList) {
    errorsList.innerHTML = errors.length ? errors.map(e => `<li>${escapeHtml(e)}</li>`).join('') : '<li>None</li>';
  }
  if (warningsList) {
    warningsList.innerHTML = warnings.length ? warnings.map(w => `<li>${escapeHtml(w)}</li>`).join('') : '<li>None</li>';
  }
}

function renderOutput() {
  const cmsOutput = document.getElementById('cmsOutput');
  const configOutput = document.getElementById('configOutput');
  if (cmsOutput) cmsOutput.textContent = generateCmsSnippetLocal();
  if (configOutput) configOutput.textContent = generateConfigJsLocal();
}

function renderCurrentStep() {
  if (currentStep === 2) renderGroupsStep();
  if (currentStep === 3) renderMappingStep();
  if (currentStep === 4) renderReview();
  if (currentStep === 5) renderOutput();
}

function renderAllDynamicSteps() {
  renderGroupsStep();
  renderMappingStep();
  if (currentStep === 4) renderReview();
  if (currentStep === 5) renderOutput();
}

function commitState(opts?: { render?: string }) {
  const render = opts?.render || 'current';
  saveState();
  if (render === 'current') renderCurrentStep();
  else if (render === 'all') renderAllDynamicSteps();
}

function download(content: string, filename: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

let modalResolve: (() => void) | null = null;
let modalEscapeHandler: ((e: KeyboardEvent) => void) | null = null;
let modalInvoker: HTMLElement | null = null;

function closeModal() {
  const overlay = document.getElementById('appModal');
  if (overlay) overlay.classList.add('hidden');
  if (modalEscapeHandler) {
    document.removeEventListener('keydown', modalEscapeHandler);
    modalEscapeHandler = null;
  }
  modalResolve = null;
  if (modalInvoker) {
    modalInvoker.focus();
    modalInvoker = null;
  }
}

function openModal(title: string, bodyHtml: string, actions: { label: string; primary?: boolean; danger?: boolean; onClick: () => void }[]) {
  const overlay = document.getElementById('appModal');
  const titleEl = document.getElementById('modalTitle');
  const bodyEl = document.getElementById('modalBody');
  const actionsEl = document.getElementById('modalActions');
  if (!overlay || !titleEl || !bodyEl || !actionsEl) return;

  modalInvoker = document.activeElement as HTMLElement;

  titleEl.textContent = title;
  bodyEl.innerHTML = bodyHtml;
  actionsEl.innerHTML = '';
  for (const a of actions) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = a.label;
    btn.className = a.danger ? 'btn-danger' : (a.primary ? 'btn-primary' : 'btn-secondary');
    btn.addEventListener('click', () => {
      a.onClick();
      if (modalResolve) modalResolve();
      closeModal();
    });
    actionsEl.appendChild(btn);
  }
  overlay.classList.remove('hidden');
  const firstFocus = overlay.querySelector<HTMLElement>('input, select, textarea, button');
  if (firstFocus) firstFocus.focus();

  if (modalEscapeHandler) {
    document.removeEventListener('keydown', modalEscapeHandler);
  }
  modalEscapeHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeModal();
    }
  };
  document.addEventListener('keydown', modalEscapeHandler);
}

function showMessage(title: string, message: string) {
  openModal(title, `<p>${escapeHtml(message)}</p>`, [{ label: 'OK', primary: true, onClick: () => {} }]);
}

function showConfirm(title: string, message: string, onConfirm: () => void, confirmLabel?: string, danger?: boolean) {
  openModal(title, `<p>${escapeHtml(message)}</p>`, [
    { label: 'Cancel', onClick: () => {} },
    { label: confirmLabel || 'Confirm', primary: !danger, danger, onClick: onConfirm },
  ]);
}

let modalInputCounter = 0;

function showInput(title: string, label: string, placeholder: string, onConfirm: (value: string) => void) {
  const id = 'modalInput' + (++modalInputCounter);
  openModal(title, `
    <div class="form-group">
      <label for="${id}">${escapeHtml(label)}</label>
      <input type="text" id="${id}" placeholder="${escapeHtml(placeholder)}" autocomplete="off">
    </div>
  `, [
    { label: 'Cancel', onClick: () => {} },
    {
      label: 'Add',
      primary: true,
      onClick: () => {
        const input = document.getElementById(id) as HTMLInputElement | null;
        onConfirm(input ? input.value.trim() : '');
      },
    },
  ]);
}
function resetDraftState() {
  localStorage.removeItem(STORAGE_KEY);
  state = createState();
  activeAddonCategoryKey = null;
  const pasteConfig = document.getElementById('pasteConfig') as HTMLTextAreaElement | null;
  if (pasteConfig) pasteConfig.value = '';
  const pasteJson = document.getElementById('pasteJson') as HTMLTextAreaElement | null;
  if (pasteJson) pasteJson.value = '';
  const ticketSearch = document.getElementById('ticketSearch') as HTMLInputElement | null;
  if (ticketSearch) ticketSearch.value = '';
  const addonSearch = document.getElementById('addonSearch') as HTMLInputElement | null;
  if (addonSearch) addonSearch.value = '';
  goToStep(1, true);
  renderAllDynamicSteps();
}

function wireEvents() {
  const a11yStatus = document.getElementById('a11yStatus');
  const ticketGroupId = document.getElementById('ticketGroupId') as HTMLInputElement | null;
  const defaultOpenCategories = document.getElementById('defaultOpenCategories') as HTMLInputElement | null;

  if (ticketGroupId) {
    ticketGroupId.addEventListener('input', e => {
      state.activeTicketGroupId = (e.target as HTMLInputElement).value.trim();
      commitState({ render: 'none' });
    });
  }

  if (defaultOpenCategories) {
    defaultOpenCategories.addEventListener('input', e => {
      state.defaultOpenCategories = (e.target as HTMLInputElement).value.split(',').map(s => s.trim()).filter(Boolean);
      for (const c of state.categories) {
        c.openDefault = state.defaultOpenCategories.includes(c.key);
      }
      commitState({ render: 'none' });
    });
  }

  const addCategoryBtn = document.getElementById('addCategoryBtn');
  if (addCategoryBtn) {
    addCategoryBtn.addEventListener('click', () => {
      addCategory('new', 'New Group', false);
      commitState({ render: 'all' });
    });
  }

  const addTicketBtn = document.getElementById('addTicketBtn');
  if (addTicketBtn) {
    addTicketBtn.addEventListener('click', () => {
      showInput('Add Ticket', 'Ticket ID', 'e.g. 402309', (id) => {
        if (id) {
          addTicket(id);
          commitState({ render: 'all' });
        }
      });
    });
  }

  const removeTicketBtn = document.getElementById('removeTicketBtn');
  if (removeTicketBtn) {
    removeTicketBtn.addEventListener('click', () => {
      const checks = document.querySelectorAll('.ticket-select:checked');
      checks.forEach(ch => {
        removeTicket((ch as HTMLElement).dataset.id || '');
      });
      commitState({ render: 'all' });
    });
  }

  const ticketSearch = document.getElementById('ticketSearch');
  if (ticketSearch) ticketSearch.addEventListener('input', () => renderMappingStep());

  const addonSearch = document.getElementById('addonSearch');
  if (addonSearch) addonSearch.addEventListener('input', () => renderMappingStep());
  const addonMappingStatusFilter = document.getElementById('addonMappingStatusFilter');
  if (addonMappingStatusFilter) addonMappingStatusFilter.addEventListener('change', () => renderMappingStep());

  const addonGroupSearch = document.getElementById('addonGroupSearch');
  if (addonGroupSearch) addonGroupSearch.addEventListener('input', () => renderAddonTable());

  const addonCategoryStatusFilter = document.getElementById('addonCategoryStatusFilter');
  if (addonCategoryStatusFilter) addonCategoryStatusFilter.addEventListener('change', () => renderAddonTable());

  const selectAllAddonsBtn = document.getElementById('selectAllAddonsBtn');
  if (selectAllAddonsBtn) {
    selectAllAddonsBtn.addEventListener('click', () => {
      if (!state.selectedTicketId) return;
      const ids = getFilteredAddonIds();
      for (const addonId of ids) {
        if (!getAddonIdsForTicket(state.selectedTicketId).has(addonId)) {
          toggleAddonForTicket(state.selectedTicketId, addonId, true);
        }
      }
      commitState({ render: 'all' });
    });
  }
  const deselectAllAddonsBtn = document.getElementById('deselectAllAddonsBtn');
  if (deselectAllAddonsBtn) {
    deselectAllAddonsBtn.addEventListener('click', () => {
      if (!state.selectedTicketId) return;
      const ids = getFilteredAddonIds();
      for (const addonId of ids) {
        if (getAddonIdsForTicket(state.selectedTicketId).has(addonId)) {
          toggleAddonForTicket(state.selectedTicketId, addonId, false);
        }
      }
      commitState({ render: 'all' });
    });
  }

  const bulkAssignBtn = document.getElementById('bulkAssignBtn');
  if (bulkAssignBtn) {
    bulkAssignBtn.addEventListener('click', () => {
      const bulkCategory = document.getElementById('bulkCategory') as HTMLSelectElement | null;
      const key = bulkCategory ? bulkCategory.value : '';
      if (!key) return;
      const checks = document.querySelectorAll('.addon-table-select:checked');
      checks.forEach(ch => {
        setAddonCategory((ch as HTMLElement).dataset.addon || '', key);
      });
      commitState({ render: 'all' });
    });
  }

  const selectAllAddonsTable = document.getElementById('selectAllAddonsTable') as HTMLInputElement | null;
  if (selectAllAddonsTable) {
    selectAllAddonsTable.addEventListener('change', e => {
      const checked = (e.target as HTMLInputElement).checked;
      document.querySelectorAll('.addon-table-select').forEach(ch => {
        (ch as HTMLInputElement).checked = checked;
        const row = ch.closest('.addon-table-row');
        if (row) row.classList.toggle('selected', checked);
      });
    });
  }

  const clearVisibleAddonsTableBtn = document.getElementById('clearVisibleAddonsTableBtn');
  if (clearVisibleAddonsTableBtn) {
    clearVisibleAddonsTableBtn.addEventListener('click', () => {
      document.querySelectorAll('.addon-table-select').forEach(ch => {
        (ch as HTMLInputElement).checked = false;
        const row = ch.closest('.addon-table-row');
        if (row) row.classList.remove('selected');
      });
      if (selectAllAddonsTable) selectAllAddonsTable.checked = false;
    });
  }

  const assignVisibleAddonsBtn = document.getElementById('assignVisibleAddonsBtn');
  if (assignVisibleAddonsBtn) {
    assignVisibleAddonsBtn.addEventListener('click', () => {
      const bulkCategory = document.getElementById('bulkCategory') as HTMLSelectElement | null;
      const key = bulkCategory ? bulkCategory.value : '';
      if (!key) return;
      document.querySelectorAll('.addon-table-select').forEach(ch => {
        setAddonCategory((ch as HTMLElement).dataset.addon || '', key);
      });
      commitState({ render: 'all' });
    });
  }

  const addAddonBtn = document.getElementById('addAddonBtn');
  if (addAddonBtn) {
    addAddonBtn.addEventListener('click', () => {
      showInput('Add Addon', 'Addon ID', 'e.g. 403323', (id) => {
        if (id) {
          addAddon(id, '');
          commitState({ render: 'all' });
        }
      });
    });
  }

  const parsePasteBtn = document.getElementById('parsePasteBtn');
  if (parsePasteBtn) {
    parsePasteBtn.addEventListener('click', () => {
      const pasteConfig = document.getElementById('pasteConfig') as HTMLTextAreaElement | null;
      const text = pasteConfig ? pasteConfig.value : '';
      const parsed = parseConfigFromText(text);
      if (parsed) {
        applyParsedConfig(parsed, state);
        showMessage('Import successful', 'Config imported successfully!');
        commitState({ render: 'all' });
      } else {
        showMessage('Import failed', 'Could not parse the pasted config. Check the format.');
      }
    });
  }

  const parseJsonBtn = document.getElementById('parseJsonBtn');
  if (parseJsonBtn) {
    parseJsonBtn.addEventListener('click', () => {
      const pasteJson = document.getElementById('pasteJson') as HTMLTextAreaElement | null;
      const text = pasteJson ? pasteJson.value : '';
      const loket = parseLoketTicketApiResponse(text);
      if (loket) {
        const summary = applyLoketImport(loket);
        const parts: string[] = [];
        if (summary.ticketNamesAdded || summary.ticketNamesUpdated) {
          const updated = summary.ticketNamesUpdated ? `, ${summary.ticketNamesUpdated} updated` : '';
          parts.push(`${summary.ticketNamesAdded} ticket name(s) added${updated}`);
        }
        if (summary.addonNamesAdded || summary.addonNamesUpdated) {
          const updated = summary.addonNamesUpdated ? `, ${summary.addonNamesUpdated} updated` : '';
          parts.push(`${summary.addonNamesAdded} addon name(s) added${updated}`);
        }
        if (summary.newTicketIds) {
          parts.push(`${summary.newTicketIds} new ticket ID(s) discovered`);
        }
        if (summary.newAddonIds) {
          parts.push(`${summary.newAddonIds} new addon ID(s) discovered`);
        }
        const message = parts.length ? parts.join('; ') + '.' : 'No changes — all IDs and names already present.';
        showMessage('Import successful', message);
        commitState({ render: 'all' });
        return;
      }
      const parsed = parseConfigFromText(text);
      if (parsed) {
        applyParsedConfig(parsed, state);
        showMessage('Import successful', 'JSON imported successfully!');
        commitState({ render: 'all' });
      } else {
        showMessage('Import failed', 'Could not parse JSON. Check the format.');
      }
    });
  }

  document.querySelectorAll('.import-mode-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.import-mode-btn').forEach(b => {
        b.classList.remove('active');
        b.setAttribute('aria-checked', 'false');
      });
      btn.classList.add('active');
      btn.setAttribute('aria-checked', 'true');
      document.querySelectorAll('.import-mode-panel').forEach(p => {
        p.classList.remove('active');
        p.setAttribute('hidden', '');
      });
      const mode = (btn as HTMLElement).dataset.mode;
      const panel = mode ? document.getElementById('panel' + mode.charAt(0).toUpperCase() + mode.slice(1)) : null;
      if (panel) {
        panel.classList.add('active');
        panel.removeAttribute('hidden');
      }
    });
  });

  const skipToGroupsBtn = document.getElementById('skipToGroupsBtn');
  if (skipToGroupsBtn) skipToGroupsBtn.addEventListener('click', () => goToStep(2));

  document.querySelectorAll('.import-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      document.querySelectorAll('.import-tab').forEach(t => {
        t.classList.remove('active');
        t.setAttribute('aria-selected', 'false');
      });
      tab.classList.add('active');
      tab.setAttribute('aria-selected', 'true');
      document.querySelectorAll('.import-panel').forEach(p => {
        p.classList.remove('active');
        p.setAttribute('hidden', '');
      });
      const tabName = (tab as HTMLElement).dataset.tab;
      const panel = tabName ? document.getElementById('panel' + tabName.charAt(0).toUpperCase() + tabName.slice(1)) : null;
      if (panel) {
        panel.classList.add('active');
        panel.removeAttribute('hidden');
      }
    });
  });

  document.querySelectorAll('.output-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      document.querySelectorAll('.output-tab').forEach(t => {
        t.classList.remove('active');
        t.setAttribute('aria-selected', 'false');
      });
      tab.classList.add('active');
      tab.setAttribute('aria-selected', 'true');
      document.querySelectorAll('.output-panel').forEach(p => {
        p.classList.remove('active');
        p.setAttribute('hidden', '');
      });
      const tabName = (tab as HTMLElement).dataset.tab;
      const panel = tabName ? document.getElementById('panel' + tabName.charAt(0).toUpperCase() + tabName.slice(1)) : null;
      if (panel) {
        panel.classList.add('active');
        panel.removeAttribute('hidden');
      }
    });
  });

  const prevBtn = document.getElementById('prevBtn');
  if (prevBtn) prevBtn.addEventListener('click', () => goToStep(currentStep - 1));

  const nextBtn = document.getElementById('nextBtn');
  if (nextBtn) nextBtn.addEventListener('click', () => goToStep(currentStep + 1));

  const copyCmsBtn = document.getElementById('copyCmsBtn');
  if (copyCmsBtn) {
    copyCmsBtn.addEventListener('click', () => {
      navigator.clipboard.writeText(generateCmsSnippetLocal()).then(() => {
        const btn = copyCmsBtn;
        const old = btn.textContent;
        btn.textContent = 'Copied!';
        if (a11yStatus) a11yStatus.textContent = 'CMS snippet copied to clipboard';
        setTimeout(() => {
          btn.textContent = old;
          if (a11yStatus) a11yStatus.textContent = '';
        }, 1500);
      });
    });
  }

  const copyConfigBtn = document.getElementById('copyConfigBtn');
  if (copyConfigBtn) {
    copyConfigBtn.addEventListener('click', () => {
      navigator.clipboard.writeText(generateConfigJsLocal()).then(() => {
        const btn = copyConfigBtn;
        const old = btn.textContent;
        btn.textContent = 'Copied!';
        if (a11yStatus) a11yStatus.textContent = 'Config copied to clipboard';
        setTimeout(() => {
          btn.textContent = old;
          if (a11yStatus) a11yStatus.textContent = '';
        }, 1500);
      });
    });
  }

  const downloadCmsBtn = document.getElementById('downloadCmsBtn');
  if (downloadCmsBtn) {
    downloadCmsBtn.addEventListener('click', () => {
      download(generateCmsSnippetLocal(), 'addon-filter-cms.html', 'text/html');
    });
  }

  const downloadConfigBtn = document.getElementById('downloadConfigBtn');
  if (downloadConfigBtn) {
    downloadConfigBtn.addEventListener('click', () => {
      download(generateConfigJsLocal(), 'addon-filter-config.js', 'application/javascript');
    });
  }

  const clearDraftBtn = document.getElementById('clearDraftBtn');
  if (clearDraftBtn) {
    clearDraftBtn.addEventListener('click', () => {
      showConfirm('Clear draft', 'The current browser draft will be removed. This cannot be undone.', () => {
        resetDraftState();
      }, 'Clear draft', true);
    });
  }

  // Modal backdrop click
  const overlay = document.getElementById('appModal');
  if (overlay) {
    overlay.addEventListener('click', e => {
      if (e.target === overlay) {
        closeModal();
      }
    });
  }
}

function init() {
  renderWizardNav();
  renderMappingStep();
  renderGroupsStep();
  goToStep(1);
  wireEvents();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
